import React, { useEffect, useState } from "react";
import { ref, onValue, remove, push, set } from "firebase/database";
import { db } from "../firebase";
import Popup from "./Popup";
import useFile from "../hooks/useFile";

const PlayerRequests = ({ gameId, onClose }) => {
  const [requests, setRequests] = useState({});
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });
  const { deleteFile } = useFile();

  useEffect(() => {
    const requestsRef = ref(db, `player-req/${gameId}/players`);
    
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      if (snapshot.exists()) {
        setRequests(snapshot.val());
      } else {
        setRequests({});
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  const handleDeleteFile = async (fileId) => {
    const result = await deleteFile(fileId);
    return result;
  };


  const handleAccept = async (requestId, playerData) => {
    setLoading(true);
    try {
      // Add player to the main players list
      const playersRef = ref(db, `games/${gameId}/players`);
      const newPlayerRef = push(playersRef);
      
      const { id: _requestId, ...playerFields } = playerData;

      await set(newPlayerRef, {
        ...playerFields,
        status: 'upcoming',
        teamId: null,
        soldPrice: null
      });

      // Remove from requests
      await remove(ref(db, `player-req/${gameId}/players/${requestId}`));

    } catch (error) {
      console.error("Error accepting player:", error);
      setPopup({
        isOpen: true,
        title: 'Error',
        message: 'Failed to accept player request. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId, playerName) => {
    setLoading(true);
    try {
      await remove(ref(db, `player-req/${gameId}/players/${requestId}`));
      if(requests[requestId]?.fileId){
        await handleDeleteFile(requests[requestId].fileId);
      }
    } catch (error) {
      console.error("Error rejecting player:", error);
      setPopup({
        isOpen: true,
        title: 'Error',
        message: 'Failed to reject player request. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const requestsArray = Object.entries(requests).map(([id, data]) => ({
    ...data,
    id
  }));

  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-inbox-fill me-2"></i>
              Player Registration Requests
              <span className="badge bg-light text-primary ms-2">
                {requestsArray.length}
              </span>
            </h5>
            <button
              className="btn-close btn-close-white"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body p-0">
            {requestsArray.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox fs-1 text-muted d-block mb-3"></i>
                <h5 className="text-muted">No Pending Requests</h5>
                <p className="text-muted">
                  All player registration requests have been processed.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>#</th>
                      <th>Photo</th>
                      <th>Name</th>
                      <th>Player Number</th>
                      <th>Role</th>
                      <th>Base Price</th>
                      <th>Mobile</th>
                      <th>Others</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestsArray.map((request, index) => (
                      <tr key={request.id}>
                        <td className="fw-semibold">{index + 1}</td>
                        <td>
                          <img
                            src={request.image || "/player.png"}
                            alt={request.name}
                            className="rounded-circle"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              border: "2px solid #dee2e6"
                            }}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold">{request.name}</div>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {request.playerNumber || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info text-dark">
                            {request.role}
                          </span>
                        </td>
                        <td className="fw-semibold text-success">
                          ৳ {request.price}
                        </td>
                        <td>
                          <small className="text-muted">
                            {request.mobileNo || "N/A"}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {request.others || "N/A"}
                          </small>
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleAccept(request.id, request)}
                              disabled={loading}
                              title="Accept Request"
                            >
                              <i className="bi bi-check-circle-fill me-1"></i>
                              Accept
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(request.id, request.name)}
                              disabled={loading}
                              title="Reject Request"
                            >
                              <i className="bi bi-x-circle-fill me-1"></i>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-footer bg-light">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div className="text-muted">
                <i className="bi bi-info-circle me-2"></i>
                <small>
                  Review and approve player registration requests
                </small>
              </div>
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                <i className="bi bi-x-lg me-1"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />
    </div>
  );
};

export default PlayerRequests;
